import { useCallback } from 'react';
const SRC = 'use-memory-sb.ts';

import { Path } from 'constants/routing';
import { RootState } from 'store/store';
import { useAppDispatch, useAppStore } from 'store/store-hooks';
import { findSbPosition, getSbTrackIndex, isNull } from 'components/app/_helpers/_app.helper';
import { useZoomCb } from 'components/app/_mediator/zoom/use-zoom-cb';
import { SB_POSITION, setSbPosition } from 'components/app/editor/_mediator/editor-controls.slice';
import { getCursorCell } from 'components/app/editor/_mediator/editor-markers/editor-markers.helper';
import { StoryBoardTracks } from 'components/app/storyboard/_mediator/sb.slice';
import { useSbCb } from 'components/app/storyboard/_mediator/use-sb-cb';
import { Snapshot, SNAPSHOT } from './memory.slice';

export const useMemorySb = () => {
  const { restoreSnapshot, zoomTo, trackElements } = useZoomCb();
  const { sbSelectClip, sbSeekTo } = useSbCb();
  const dispatch = useAppDispatch();
  const store = useAppStore();

  return useCallback(() => {
    const { activeClip, storyBoard, duration, markers, cells, fcCells, cameFrom, sbSnapshot, fcSnapshot } = selectData(
      store.getState()
    );

    const restoreSb = () => {
      const snapshot = sbSnapshot ?? SNAPSHOT;
      sbSeekTo(snapshot.cursorTime);
      restoreSnapshot(snapshot);
    };

    const syncTracks = (snapshot: Snapshot, selectedTrack: StoryBoardTracks) => {
      zoomTo(snapshot.zoom);
      if (!sbSnapshot) return;
      const zoomRatio = snapshot.zoom / sbSnapshot.zoom;
      trackElements.current.forEach((track, index) => {
        if (index === getSbTrackIndex(selectedTrack)) return;
        track[0].scrollLeft = sbSnapshot.trackPositions[index] * zoomRatio;
      });
    };

    if (!duration || !markers || !cells) {
      console.error(`[${SRC}] useMemorySb`);
      return;
    }

    if (cameFrom === Path.finalcuts) {
      if (!fcSnapshot || !fcCells) return;
      dispatch(setSbPosition(SB_POSITION));
      sbSeekTo(fcSnapshot.cursorTime);
      syncTracks(fcSnapshot, StoryBoardTracks.storyBoard);
    } else {
      if (isNull(activeClip)) {
        restoreSb();
        return;
      }
      const position = findSbPosition(activeClip, storyBoard);
      if (!position) {
        console.error(`[${SRC}] useMemoryStoryboard: position`);
        return;
      }
      sbSelectClip(activeClip, position.cell, position.track);

      if (sbSnapshot) {
        const cell = getCursorCell(sbSnapshot.cursorTime, markers);
        if (cell === position.cell && (markers[cell - 1] ?? 0) !== sbSnapshot.cursorTime)
          sbSeekTo(sbSnapshot.cursorTime);
        syncTracks(sbSnapshot, position.track);
      }
    }
  }, []);
};

/* S E L E C T O R */

const selectData = (state: RootState) => {
  const { cameFrom, storyboard: sbSnapshot, finalcut: fcSnapshot } = state.memory;
  const { activeClip, storyBoard, markers, cells } = state.editor;
  const { fcCells } = state.fc__data;
  const { duration } = state.audio;

  return {
    activeClip,
    storyBoard,
    duration,
    markers,
    cells,
    fcCells,
    cameFrom,
    sbSnapshot,
    fcSnapshot,
  };
};
